import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import type { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import BlockCard from '@components/block-card'
import Container from '@components/container'
import DataHandler from '@components/data-handler'
import FILTER_INITIAL_STATE from '@components/filter/filterState'
import ProjectSwitchToggle from '@components/project-switch/ProjectSwitchToggle'

import { useGetBuildingQuerySilent } from '@api/building'
import {
  Polygon,
  selectFromResult as selectInteractiveFromResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import AreaSkeleton from './area-skeleton'
import BlockList from './block-list'

export interface AreaViewProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  pageTitle: string
}

function AreaView({ projectIdentity, session, pageTitle }: AreaViewProps) {
  const location = useLocation()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const [areaViews, setAreaViews] = useState<Array<Polygon>>([])
  const [blocks, setBlocks] = useState<Array<string>>([])

  const [isAreaDataReady, setIsAreaDataReady] = useState<boolean>(false)

  const interactiveQueryResult = useGetInteractivePlanQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult: selectInteractiveFromResult }
  )

  const buildingQueryResult = useGetBuildingQuerySilent({
    projectName: projectIdentity.projectId,
  })

  const updateTriggerBuilding = (state: boolean) =>
    firebaseControlQuery.update({
      [`areaView.triggerBuilding`]: state,
    })

  const handleMapClick = async (groupId: string) => {
    if (blocks.length > 1) {
      const blockId = blocks.includes(groupId) ? groupId : blocks[0]
      await firebaseControlQuery.update({
        [`building.activeBlock`]: blockId,
      })
    }
    await updateTriggerBuilding(true)
  }

  const resetBuildingMap = () =>
    firebaseControlQuery.update({
      [`building.activeBlock`]: '',
      [`building.activeLevel`]: '',
      [`building.activeUnit`]: '',
      [`building.unitGalleryIndex`]: 0,
    })

  const resetBuildingView = () =>
    firebaseControlQuery.update({
      [`building.sidePanelFolded`]: true,
      [`building.unitFilter`]: FILTER_INITIAL_STATE,
    })

  const redirectToLevelView = () => {
    const { maps } = interactiveQueryResult
    const { blockOrders } = buildingQueryResult

    const blockSource =
      blockOrders?.length > 1 ? blockOrders : Object.keys(maps?.blocks || {})
    const firstBlockKey = blockSource.length > 1 ? blockSource[0] : ''

    firebaseControlQuery.update({
      [`building.activeBlock`]: firstBlockKey,
    })
    firebaseControlQuery.updateRoute('level-view')
  }

  const shouldRedirectToLevelView = useMemo(() => {
    const { maps, isLoaded } = interactiveQueryResult
    const { isLoaded: isBuildingLoaded } = buildingQueryResult

    return (
      isLoaded &&
      isBuildingLoaded &&
      !maps?.areaView?.image &&
      Object.keys(maps?.blocks || {}).length > 0
    )
  }, [interactiveQueryResult.isLoaded, buildingQueryResult.isLoaded])

  useLayoutEffect(() => {
    if (shouldRedirectToLevelView) {
      redirectToLevelView()
    }
  }, [shouldRedirectToLevelView, location])

  useEffect(() => {
    const { maps, isLoaded, isError } = interactiveQueryResult
    if (isLoaded && !isError && Object.keys(maps).length > 0) {
      if (areaViews.length === 0) {
        if (maps.areaView?.polygons) {
          setAreaViews(maps.areaView.polygons.map((res: Polygon) => res))
          setIsAreaDataReady(true)
        }
      }
    }
  }, [interactiveQueryResult.isLoaded, areaViews])

  useEffect(() => {
    const { maps } = interactiveQueryResult
    const { isLoaded, isError, blockOrders, isSuccess } = buildingQueryResult

    if (isLoaded && isSuccess && !isError) {
      if (maps?.blocks) {
        setBlocks(
          blockOrders?.length > 1
            ? blockOrders
            : Object.keys(maps?.blocks || {})
        )
      }
    }
  }, [buildingQueryResult, interactiveQueryResult.isLoaded])

  useEffect(() => {
    if (session) {
      const {
        areaView: { triggerBuilding },
        building: { activeLevel, activeUnit },
      } = session
      if (activeLevel || activeUnit) {
        resetBuildingMap()
      }
      if (triggerBuilding) {
        setTimeout(async () => {
          await updateTriggerBuilding(false)
          await resetBuildingView()
          await firebaseControlQuery.updateRoute('level-view')
        }, 1000)
      }
    }
  }, [session])

  return (
    <Container>
      <div className="h-full w-full px-4">
        <DataHandler
          payloads={[
            {
              name: 'interactiveQueryResult',
              payload: {
                ...interactiveQueryResult,
                data: interactiveQueryResult.maps,
              },
            },
            {
              name: 'buildingQueryResult',
              payload: {
                ...buildingQueryResult,
                data: buildingQueryResult.blockOrders || [],
              },
            },
          ]}
          showDefaultNotFound={!shouldRedirectToLevelView}
          skeletonFrame={<AreaSkeleton />}
        >
          {isAreaDataReady && (
            <>
              <div className="flex h-top-bar w-full items-end text-title font-medium text-neutralColour">
                <div className="pb-5">{pageTitle}</div>
              </div>
              <div className="no-scrollbar h-page-body w-full overflow-auto">
                {projectIdentity.remoteInteractiveBuilding.areaView
                  .useMatrix ? (
                  <BlockList
                    projectId={projectIdentity.projectId}
                    handleBlockClick={(argBlock: string) =>
                      handleMapClick(argBlock)
                    }
                  />
                ) : (
                  areaViews.map((res) => (
                    <BlockCard
                      key={res.groupId}
                      label={res.label || res.groupId}
                      handleClick={() => handleMapClick(res.groupId)}
                    />
                  ))
                )}
              </div>
            </>
          )}
          <ProjectSwitchToggle
            className="absolute bottom-5 left-5 z-3"
            showOverlayOnProcessing
          />
        </DataHandler>
      </div>
    </Container>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    whiteLabel,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    pageTitle:
      mapWhiteLabelsWithDefaults(whiteLabel)?.remote?.areaView?.pageTitle,
  })
)(AreaView)
